/* =============================================================================
   New PDP — Buy Box behaviour
   -----------------------------------------------------------------------------
   - edition selection (each edition is a separate PRODUCT, not a variant)
   - purchase option selection (subscribe / one time) + selling plan
   - promo-discount aware price rendering
   - quantity stepper + AJAX add to cart -> theme cart drawer (never a redirect)
   - FAQ accordion
   - mobile media slider dots

   init() is idempotent and re-runs on shopify:section:load so the section keeps
   working while it is being edited in the theme editor.
   ============================================================================= */
(function () {
  'use strict';

  function money(cents, format, noDecimals) {
    if (cents == null || isNaN(cents)) return '';
    var value = cents / 100;
    if (noDecimals) value = Math.round(value);

    function withCommas(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    function withDots(n, decimals) {
      return withCommas(n, decimals).replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.');
    }

    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withCommas(value, noDecimals ? 0 : 2);
        case 'amount_no_decimals': return withCommas(value, 0);
        case 'amount_with_comma_separator': return withDots(value, noDecimals ? 0 : 2);
        case 'amount_no_decimals_with_comma_separator': return withDots(value, 0);
        case 'amount_with_apostrophe_separator': return withCommas(value, noDecimals ? 0 : 2).replace(/,/g, "'");
        default: return withCommas(value, noDecimals ? 0 : 2);
      }
    });
  }

  /* Open the theme's cart drawer.
     This must NEVER navigate. A previous version fell back to clicking the first
     cart link it could find — in this theme that is a real header anchor, which
     is why the drawer opened and the page then jumped away. Only the theme's own
     cart icon is safe to click, because it preventDefaults its own handler. */
  function openCartDrawerSafely(attempt) {
    attempt = attempt || 0;

    if (typeof window.updateCartCount === 'function') {
      try { window.updateCartCount(); } catch (e) { /* optional theme helper */ }
    }

    if (typeof window.openCartDrawer === 'function') {
      try { window.openCartDrawer(true); }
      catch (e) { console.error('[kpdp] openCartDrawer threw', e); }
      return;
    }

    /* new-cart-drawer.js is deferred; on a very fast click it may not be up yet. */
    if (attempt < 25) {
      setTimeout(function () { openCartDrawerSafely(attempt + 1); }, 80);
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    var icon = document.querySelector('.js-drawer-open-cart');
    if (icon) icon.click();
    else console.warn('[kpdp] cart drawer unavailable - item was added, staying put');
  }

  function KpdpBuyBox(root) {
    this.root = root;
    this.form = root.querySelector('form.kpdp-bb__form')
             || root.querySelector('form[action*="/cart/add"]');
    this.data = { editions: [], labels: {} };
    try {
      var el = root.querySelector('[data-kpdp-data]');
      if (el) this.data = JSON.parse(el.textContent);
    } catch (e) {
      console.error('[kpdp] could not parse product data', e);
    }
    if (!this.data.editions || !this.data.editions.length) this.data.editions = [];

    this.state = {
      edition: this.data.default_edition || 0,
      plan: null,
      purchase: 'onetime',
      qty: 1
    };
    if (this.state.edition >= this.data.editions.length) this.state.edition = 0;

    var currentHandle = this.data.current_handle;
    if (currentHandle) {
      for (var i = 0; i < this.data.editions.length; i++) {
        if (this.data.editions[i].handle === currentHandle) {
          this.state.edition = i;
          break;
        }
      }
    }

    this.bind();
    this.syncFromDom();
    this.update();
  }

  KpdpBuyBox.prototype.edition = function () {
    return this.data.editions[this.state.edition] || null;
  };

  /* Selling plans belong to a product, so a plan id from one edition is
     meaningless on another. Honour an explicit choice only when the active
     edition actually offers it; otherwise fall back to that edition's own plan. */
  KpdpBuyBox.prototype.planFor = function (ed) {
    if (!ed) return null;
    var allocs = ed.allocations || [];
    if (this.state.plan) {
      for (var i = 0; i < allocs.length; i++) {
        if (String(allocs[i].selling_plan_id) === String(this.state.plan)) return this.state.plan;
      }
    }
    return ed.selling_plan_id || (allocs[0] && allocs[0].selling_plan_id) || null;
  };

  KpdpBuyBox.prototype.canSubscribe = function (ed) {
    return !!(ed && (ed.selling_plan_id || (ed.allocations && ed.allocations.length)));
  };

  KpdpBuyBox.prototype.pricesFor = function (ed, purchase, planId) {
    if (!ed) return { price: null, compare: null };
    var price = ed.price;
    if (purchase === 'subscribe') {
      var wanted = planId || ed.selling_plan_id;
      var allocs = ed.allocations || [];
      for (var i = 0; i < allocs.length; i++) {
        if (String(allocs[i].selling_plan_id) === String(wanted)) { price = allocs[i].price; break; }
      }
    }
    var compare = ed.compare_at_price && ed.compare_at_price > ed.price ? ed.compare_at_price : ed.price;

    var promo = this.data.promo || {};
    if (promo.enabled && promo.amount > 0 &&
        (promo.applies_to === 'both' || promo.applies_to === purchase)) {
      price = Math.max(0, price - promo.amount);
    }
    if (compare <= price) compare = null;
    return { price: price, compare: compare };
  };

  KpdpBuyBox.prototype.syncFromDom = function () {
    var checked = this.root.querySelector('[data-kpdp-purchase]:checked');
    if (checked) this.state.purchase = checked.value;
    var qtyEl = this.root.querySelector('[data-kpdp-qty-value]');
    if (qtyEl) this.state.qty = parseInt(qtyEl.textContent, 10) || 1;
  };

  KpdpBuyBox.prototype.bind = function () {
    var self = this;

    this.root.querySelectorAll('[data-kpdp-edition]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.edition = parseInt(btn.dataset.kpdpEdition, 10) || 0;
        self.update();
      });
    });

    this.root.querySelectorAll('[data-kpdp-purchase]').forEach(function (input) {
      input.addEventListener('change', function () {
        self.state.purchase = input.value;
        self.update();
      });
    });

    var freq = this.root.querySelector('[data-kpdp-frequency]');
    if (freq) {
      freq.addEventListener('change', function () {
        self.state.plan = freq.value;
        var sub = self.root.querySelector('[data-kpdp-purchase][value="subscribe"]');
        if (sub) { sub.checked = true; self.state.purchase = 'subscribe'; }
        self.update();
      });
    }

    this.root.querySelectorAll('[data-kpdp-qty]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.state.qty = Math.max(1, self.state.qty + (btn.dataset.kpdpQty === 'up' ? 1 : -1));
        self.update();
      });
    });

    /* The click is the only path that adds. Capture phase + stopImmediatePropagation
       so Recharge / Koin / theme handlers on the same button never also fire. */
    var cta = this.root.querySelector('[data-kpdp-cta]');
    if (cta) {
      cta.addEventListener('click', function (event) {
        /* the quantity stepper sits inside the pill; those clicks are not "add" */
        if (event.target.closest && event.target.closest('.kpdp-bb__qty')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        self.addToCart();
      }, true);
    }

    /* Defensive only: the button is type=button, so nothing here submits by itself.
       This catches a programmatic or keyboard submit from a third-party script and
       stops it before the browser can do a native POST to /cart/add. */
    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);
    }

    this.root.querySelectorAll('[data-kpdp-faq]').forEach(function (item) {
      var btn = item.querySelector('[data-kpdp-faq-q]');
      var panel = item.querySelector('[data-kpdp-faq-a]');
      if (!btn || !panel) return;
      /* One handler per panel, tracked so a fast click can't leave a stale
         "set height:auto" listener behind — that was making a closing panel
         snap back open, which reads as "the FAQ doesn't expand properly". */
      var settle = null;
      function clearSettle() {
        if (settle) { panel.removeEventListener('transitionend', settle); settle = null; }
      }
      btn.addEventListener('click', function () {
        var open = item.dataset.open === 'true';
        clearSettle();
        panel.style.height = panel.scrollHeight + 'px';

        if (open) {
          requestAnimationFrame(function () { panel.style.height = '0px'; });
        } else {
          settle = function (e) {
            if (e.target !== panel || e.propertyName !== 'height') return;
            if (item.dataset.open === 'true') panel.style.height = 'auto';
            clearSettle();
          };
          panel.addEventListener('transitionend', settle);
          /* if the transition never fires (reduced motion, hidden tab), settle anyway */
          setTimeout(function () {
            if (settle && item.dataset.open === 'true') { panel.style.height = 'auto'; clearSettle(); }
          }, 400);
        }
        item.dataset.open = open ? 'false' : 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });

    this.bindSlider();
    this.bindVideo();
  };

  KpdpBuyBox.prototype.visibleGallery = function () {
    return this.root.querySelector('[data-kpdp-gallery]:not([hidden])');
  };

  /* Delegation so edition swaps do not stack listeners on each gallery. */
  KpdpBuyBox.prototype.bindSlider = function () {
    var media = this.root.querySelector('.kpdp-bb__media');
    if (!media || media.dataset.kpdpSliderBound === 'true') return;
    media.dataset.kpdpSliderBound = 'true';

    media.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-kpdp-dot]');
      if (!dot) return;
      var gallery = dot.closest('[data-kpdp-gallery]') || media;
      var track = gallery.querySelector('[data-kpdp-track]');
      if (!track) return;
      var dots = gallery.querySelectorAll('[data-kpdp-dot]');
      var i = Array.prototype.indexOf.call(dots, dot);
      var cell = track.children[i];
      if (cell) track.scrollTo({ left: cell.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    });

    var ticking = false;
    media.addEventListener('scroll', function (e) {
      var track = e.target.closest ? e.target.closest('[data-kpdp-track]') : null;
      if (!track || e.target !== track) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var gallery = track.closest('[data-kpdp-gallery]') || media;
        var dots = gallery.querySelectorAll('[data-kpdp-dot]');
        var i = Math.round(track.scrollLeft / (track.clientWidth || 1));
        dots.forEach(function (d, di) { d.setAttribute('aria-current', di === i ? 'true' : 'false'); });
        ticking = false;
      });
    }, true);
  };

  KpdpBuyBox.prototype.setVideoUi = function (wrap, playing) {
    if (!wrap) return;
    wrap.classList.toggle('is-playing', playing);
    var btn = wrap.querySelector('[data-kpdp-play]');
    if (!btn) return;
    btn.classList.toggle('is-playing', playing);
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    btn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  };

  KpdpBuyBox.prototype.pauseVideo = function (wrap) {
    if (!wrap) return;
    var video = wrap.querySelector('video');
    if (video) {
      video.pause();
      try { video.currentTime = 0; } catch (e) { /* ignore seek errors */ }
    }
    this.setVideoUi(wrap, false);
  };

  KpdpBuyBox.prototype.toggleVideo = function (wrap) {
    var self = this;
    var video = wrap && wrap.querySelector('video');
    if (!video) return;

    if (!video.paused) {
      video.pause();
      this.setVideoUi(wrap, false);
      return;
    }

    video.muted = false;
    this.setVideoUi(wrap, true);
    var playPromise = video.play();
    if (playPromise && playPromise.then) {
      playPromise.catch(function () { self.setVideoUi(wrap, false); });
    }
  };

  KpdpBuyBox.prototype.bindVideo = function () {
    var self = this;
    if (this.root.dataset.kpdpVideoBound === 'true') return;
    this.root.dataset.kpdpVideoBound = 'true';

    this.root.querySelectorAll('.kpdp-bb__video video').forEach(function (video) {
      video.setAttribute('playsinline', '');
      video.playsInline = true;
      video.removeAttribute('autoplay');
    });

    this.root.addEventListener('click', function (e) {
      var wrap = e.target.closest('.kpdp-bb__video');
      if (!wrap) return;
      if (!wrap.querySelector('video')) return;
      e.preventDefault();
      self.toggleVideo(wrap);
    });
  };

  KpdpBuyBox.prototype.update = function () {
    var self = this;
    var ed = this.edition();
    var format = this.data.money_format;
    var noDec = !!this.data.hide_decimals;

    this.root.querySelectorAll('[data-kpdp-edition]').forEach(function (btn) {
      var on = parseInt(btn.dataset.kpdpEdition, 10) === self.state.edition;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    var editionChanged = this._lastEdition !== this.state.edition;
    this._lastEdition = this.state.edition;

    if (editionChanged) {
      this.root.querySelectorAll('[data-kpdp-gallery]').forEach(function (g) {
        var on = parseInt(g.dataset.kpdpGallery, 10) === self.state.edition;
        g.hidden = !on;
        if (!on) {
          g.querySelectorAll('.kpdp-bb__video').forEach(function (wrap) {
            self.pauseVideo(wrap);
          });
        }
      });

      var visible = this.visibleGallery();
      if (visible) {
        var track = visible.querySelector('[data-kpdp-track]');
        if (track) track.scrollLeft = 0;
        visible.querySelectorAll('[data-kpdp-dot]').forEach(function (d, di) {
          d.setAttribute('aria-current', di === 0 ? 'true' : 'false');
        });
      }

      var titleEl = this.root.querySelector('[data-kpdp-title]');
      if (titleEl && ed && ed.title) titleEl.textContent = ed.title;

      if (this.data.update_url && ed && ed.url) {
        try {
          var next = new URL(ed.url, window.location.origin);
          next.search = window.location.search;
          var dest = next.pathname + next.search + window.location.hash;
          var here = window.location.pathname + window.location.search + window.location.hash;
          if (dest !== here) history.replaceState({ kpdpEdition: this.state.edition }, '', dest);
        } catch (err) { /* ignore malformed product urls */ }
      }
    }

    /* Editions are separate products; one may sell subscriptions and another not. */
    if (!this.canSubscribe(ed) && this.state.purchase === 'subscribe') this.state.purchase = 'onetime';

    this.root.querySelectorAll('[data-kpdp-plan-card]').forEach(function (card) {
      var purchase = card.dataset.purchase;
      if (purchase === 'subscribe') card.hidden = !self.canSubscribe(ed);
      var planId = purchase === 'subscribe' ? self.planFor(ed) : null;
      var pr = self.pricesFor(ed, purchase, planId);

      var priceEl = card.querySelector('[data-kpdp-price]');
      var cmpEl = card.querySelector('[data-kpdp-compare]');
      if (priceEl) priceEl.textContent = money(pr.price, format, noDec);
      if (cmpEl) {
        cmpEl.textContent = pr.compare ? money(pr.compare, format, noDec) : '';
        cmpEl.hidden = !pr.compare;
      }
      var selected = purchase === self.state.purchase;
      card.dataset.selected = selected ? 'true' : 'false';
      var input = card.querySelector('[data-kpdp-purchase]');
      if (input) input.checked = selected;
    });

    var idInput = this.root.querySelector('[data-kpdp-variant-id]');
    if (idInput && ed) idInput.value = ed.variant_id;

    var planInput = this.root.querySelector('[data-kpdp-selling-plan]');
    if (planInput) {
      var plan = this.state.purchase === 'subscribe' ? this.planFor(ed) : null;
      if (plan) { planInput.value = plan; planInput.disabled = false; }
      else { planInput.value = ''; planInput.disabled = true; }
    }

    var freq = this.root.querySelector('[data-kpdp-frequency]');
    if (freq && ed) {
      var allocs = ed.allocations || [];
      var signature = allocs.map(function (a) { return a.selling_plan_id; }).join(',');
      if (freq.dataset.kpdpPlans !== signature) {
        freq.innerHTML = '';
        allocs.forEach(function (a) {
          var o = document.createElement('option');
          o.value = a.selling_plan_id;
          o.textContent = a.name || ('Every ' + a.selling_plan_id);
          freq.appendChild(o);
        });
        freq.dataset.kpdpPlans = signature;
      }
      var resolved = this.planFor(ed);
      if (resolved) freq.value = String(resolved);
    }

    var qtyEl = this.root.querySelector('[data-kpdp-qty-value]');
    if (qtyEl) qtyEl.textContent = this.state.qty;
    var qtyInput = this.root.querySelector('[data-kpdp-qty-input]');
    if (qtyInput) qtyInput.value = this.state.qty;

    var cta = this.root.querySelector('[data-kpdp-cta]');
    var label = this.root.querySelector('[data-kpdp-cta-label]');
    if (cta) {
      var sellable = ed && ed.available;
      cta.disabled = !sellable;
      if (label) label.textContent = sellable ? this.data.labels.add : this.data.labels.sold_out;
    }
  };

  KpdpBuyBox.prototype.addToCart = function () {
    var self = this;
    var ed = this.edition();
    if (!ed || this.adding) return;      /* one request per click */
    this.adding = true;

    var cta = this.root.querySelector('[data-kpdp-cta]');
    var label = this.root.querySelector('[data-kpdp-cta-label]');
    var original = label ? label.textContent : '';

    if (cta) cta.disabled = true;
    if (label) label.textContent = this.data.labels.adding;

    var payload = { id: ed.variant_id, quantity: this.state.qty };
    var planInput = this.root.querySelector('[data-kpdp-selling-plan]');
    if (planInput && !planInput.disabled && planInput.value) payload.selling_plan = planInput.value;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Add to cart failed'); });
        return r.json();
      })
      .then(function () { openCartDrawerSafely(); })
      .catch(function (err) {
        console.error('[kpdp] add to cart:', err);
        if (label) label.textContent = self.data.labels.error || 'Try again';
      })
      .finally(function () {
        self.adding = false;
        if (cta) cta.disabled = false;
        if (label) setTimeout(function () { label.textContent = original; }, 1200);
      });
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-kpdp-buybox]').forEach(function (root) {
      if (root.dataset.kpdpReady === 'true') return;
      root.dataset.kpdpReady = 'true';
      new KpdpBuyBox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-kpdp-buybox]').forEach(function (r) { delete r.dataset.kpdpReady; });
  });

  window.KpdpBuyBox = { init: init };
})();
